import { NotificationService } from '../../../../shared/services/notification.service';
import { Component, Input, OnInit } from '@angular/core';
import { LeadDetailParticipantInfoComponent } from "../lead-detail-participant-info/lead-detail-participant-info.component";
import { LeadDetailCommMethodsComponent } from "../lead-detail-comm-methods/lead-detail-comm-methods.component";
import { LeadDetailAddressInfoComponent } from "../lead-detail-address-info/lead-detail-address-info.component";
import { LeadDetailCommunicationInfoComponent } from "../lead-detail-communication-info/lead-detail-communication-info.component";
import { BaseComponent } from '../../../../shared/utils/base.component';
import { SectionsService } from '../../../../services/sections.service';
import { CommonModule } from '@angular/common';
import { LeadDetailsService } from '../../../../services/lead-details.service';
import { STATIC_DATA, STATIC_JSON_DATA_PATH } from '../../../../shared/constants/static-data.constants';
import { LeadDetailIdentifiersComponent } from "../lead-detail-identifiers/lead-detail-identifiers.component";
import { LeadApplicationDetailsComponent } from "../lead-application-details/lead-application-details.component";
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { LoggerService } from '../../../../shared/services/logger.service';
import { AccordionComponent } from '@ppl/ui';
import { AuthorizeDirective } from '../../../../shared/authorization/authorize.directive.';
import { MEMBER_VIEW_PAGE_IDS, PAGE_CONSTANTS, TABS } from '../../../../shared/constants/page.constants';
import { LeadDetailProfessionalLicensesComponent } from '../lead-detail-professional-licenses/lead-detail-professional-licenses.component';
import { LeadDetailServicesComponent } from '../lead-detail-services/lead-detail-services.component';
import { LeadDetailEligibilityDatesComponent } from '../lead-detail-eligibility-dates/lead-detail-eligibility-dates.component';
import { LeadDetailViewerComponent } from "../lead-detail-viewer/lead-detail-viewer.component";
import { ReferralDataService } from '../../../../services/referral-data.service';
import { LeadAdditionalDemographicsComponent } from "../lead-additional-demographics/lead-additional-demographics.component";
import { UserService } from '../../../../services/user.service';
import { LeadPaymentMethodComponent } from "../lead-payment-method/lead-payment-method.component";
import { LoginDetailsComponent } from "../../login-details/login-details.component";
import { LeadFmsDataComponent } from "../lead-fms-data/lead-fms-data.component";
import { LeadPmpmListComponent } from "../lead-pmpm-list/lead-pmpm-list.component";
import { ProviderClassificationComponent } from "../provider-classification/provider-classification.component";
import { LeadDetailParentGuardianComponent } from "../lead-detail-parent-guardian/lead-detail-parent-guardian.component";
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';

@Component({
  selector: 'ppl-lead-information',
  standalone: true,
  imports: [NgZorroModule, LeadDetailParticipantInfoComponent,
    CommonModule, LeadDetailCommMethodsComponent, LeadDetailAddressInfoComponent,
    LeadDetailCommunicationInfoComponent, LeadDetailIdentifiersComponent, LeadDetailProfessionalLicensesComponent,
    LeadApplicationDetailsComponent, AuthorizeDirective, AccordionComponent, LeadDetailServicesComponent, LeadDetailEligibilityDatesComponent, LeadDetailViewerComponent, LeadAdditionalDemographicsComponent, LeadPaymentMethodComponent, LoginDetailsComponent, LeadFmsDataComponent, LeadPmpmListComponent, ProviderClassificationComponent, LeadDetailParentGuardianComponent],
  templateUrl: './lead-information.component.html',
  styleUrl: './lead-information.component.css'
})
export class LeadInformationComponent extends BaseComponent implements OnInit {
  filledInfo: any;
  steps: any;
  entry  = 'base';
  sectionDataPath: string = STATIC_JSON_DATA_PATH.LEAD_DETAILS_SECTION_DATA;
  providerEnrollmentSectionDataPath: string = STATIC_JSON_DATA_PATH.LEAD_DETAILS_PROVIDER_ENROLLMENT_SECTION_DATA;
  source!: string;
  pageId = '';
  tabId = TABS.EnrollmentInformation;
  currentRoute = '';
  addressInfo: any;
  program: any;
  selectedLead: any;
  contactMethodsModifiedDate: any;
  contactDetailsInfo: any;
  identifiersModifiedDate = '';
  professionalLicense: any;
  professionalLicensePath: string = STATIC_DATA.LEAD_PROF_LICENCES;
  servicesRendered : string = STATIC_DATA.LEAD_SERVICES_RENDERED;
  status = '';
  title: any;
  enrollmentType: any;
  isParticipantFlow = true;
  isProviderFlow = false;
  isPA = false;
  userType: string = '';
  userCategory: string = '';

  /** From getProviderEnrollmentData — drives the Provider Classification section. */
  participantAssociations: any[] = [];

  /** Provider Classification is only asked of vendors and independent contractors. */
  get isClassificationProvider(): boolean {
    return !!this.classificationUserType;
  }

  /**
   * Taken from the provider's participant associations. A provider associated as both
   * a vendor and an independent contractor is classified as a vendor.
   */
  get classificationUserType(): string {
    const providerTypes = this.participantAssociations.map((association: any) => association?.providerType);

    if (providerTypes.includes('VE')) {
      return 'VE';
    }

    if (providerTypes.includes('IC')) {
      return 'IC';
    }

    if (providerTypes.includes('IF')) {
      return 'IF';
    }

    return '';
  }

  @Input() readOnly = false;

  constructor (
    private sectionsService: SectionsService, 
    private leadDetailsService: LeadDetailsService, 
    private notificationService: NotificationService, 
    private logger: LoggerService,
    private referralDataService: ReferralDataService,
    private userService: UserService,
    private dateTimeFormatterService: DateTimeFormatterService
  ) {
    super();
  }

  /** Show the Parent/Guardian section only for a minor participant (age < 18 from dob). */
  get isParticipantMinor(): boolean {
    const dob = this.filledInfo?.dob;
    if (!dob || !this.isParticipantFlow) return false;
    try {
      return this.getAge(this.dateTimeFormatterService.formatStringToDate(dob)) < 18;
    } catch {
      return false;
    }
  }

  ngOnInit() {
    const providerType = this.sectionsService.getEnrollmentType();
    this.pageId = MEMBER_VIEW_PAGE_IDS[this.userService.getFirstSegment()!];
    this.source = 'ppl-lead-information';
    this.entry = this.userService.getLeadEntry() ?? '';
    this.title = this.entry == 'referral' ? 'Basic Information' : 'Demographics';
    this.isParticipantFlow = this.userService.getLeadEntry() == 'participantEnrollment' || this.sectionsService.getFlow() == 'participantEnrollment';

    this.isProviderFlow = this.userService.getLeadEntry() == 'providerEnrollment' || this.sectionsService.getFlow() == 'providerEnrollment';                     
    this.program = this.userService.getSelectedProgram();
    if (providerType == 'provider') {
      this.isPA = true;
    }

    try{
      if(this.entry === STATIC_DATA.PROVIDER_ENROLLMENT){
        this.enrollmentType = this.sectionsService.getEnrollmentType();
      }
    }catch(e){
      this.logger.info(this.source, e)
    }
    
    this.getSectionData();
    
    try {
      this.selectedLead = JSON.parse(this.userService.getSelectedLead()!);
    } catch {
      this.notificationService.alert("error", "Error Fetching Data", "Error getting selected lead data");
    }
    //const selectedLead: any = JSON.parse(this.userService.getSelectedLead()!);
    
    this.getLeadData(false);
    if (this.entry === STATIC_DATA.REFERRAL){
      this.userType = 'participant';
      this.userCategory = STATIC_DATA.REFERRAL;
    }
    else if(this.entry == STATIC_DATA.PARTICIPANT_ENROLLMENT){
      this.userType = 'participant';
      this.userCategory = 'enrollment';
    }
    else if(this.entry == STATIC_DATA.PROVIDER_ENROLLMENT){
      this.userType = 'provider';
      this.userCategory = 'enrollment';
    }
    else{
      this.userType = this.entry;
      this.userCategory = 'lead';
    }

    this.leadDetailsService.getUpdatedLeadData().subscribe((data) => {
      this.filledInfo =
        data?.responsedata?.infoData;
        this.participantAssociations = data?.responsedata?.participantAssociations ?? this.participantAssociations;
        this.leadDetailsService.setLeadStatus(this.filledInfo?.applicationStatus);


        // if entry is provider we need to load steps based on the data we are geting from server to decide the provider type.
        // fetching the entry again to make sure we have the latest value, as it is a subscription and entry can be changed before and after update.
        this.entry = this.userService.getLeadEntry() ?? '';
        if (this.entry === STATIC_DATA.PROVIDER){
          this.getSectionData();
        }
      });
  }

  async getSectionData(){
    try{
      if(this.entry){
        if(this.entry == STATIC_DATA.PROVIDER_ENROLLMENT){
          this.steps = await this.sectionsService.getJsonConfigurationData(this.providerEnrollmentSectionDataPath, this.entry, this.enrollmentType);
        } else{
          if (this.entry == STATIC_DATA.PROVIDER){
            if (this.filledInfo?.isIC && this.filledInfo?.isIR){
              this.steps = await this.sectionsService.getJsonConfigurationData(STATIC_JSON_DATA_PATH.PROVIDER_LEAD_DETAILS_IP_IC_SECTION_DATA, this.entry, this.enrollmentType);
            }
            else if (this.filledInfo?.isIC){
              this.steps = await this.sectionsService.getJsonConfigurationData(STATIC_JSON_DATA_PATH.PROVIDER_LEAD_DETAILS_IC_SECTION_DATA, this.entry, this.enrollmentType);
            }
            else if (this.filledInfo?.isIR){
              this.steps = await this.sectionsService.getJsonConfigurationData(STATIC_JSON_DATA_PATH.PROVIDER_LEAD_DETAILS_IP_SECTION_DATA, this.entry, this.enrollmentType);
            }
            else if (this.filledInfo?.isVendor){
              this.steps = await this.sectionsService.getJsonConfigurationData(STATIC_JSON_DATA_PATH.PROVIDER_LEAD_DETAILS_Vendor_SECTION_DATA, this.entry, this.enrollmentType);
            }
            else{
              this.steps = await this.sectionsService.getJsonConfigurationData(this.sectionDataPath, this.entry, this.enrollmentType);
            }
          }
          else{
            this.steps = await this.sectionsService.getJsonConfigurationData(this.sectionDataPath, this.entry, this.enrollmentType);
          }
        }
      
      /* console.log(this.steps); */
      this.secData = this.steps[STATIC_DATA.PARTICIPANT_INFO];
      /* console.log(this.secData);
      this.logger.debug(this.source, "Logger test successful", this.steps); */
      }
    }
    catch(e){
      this.logger.debug("Error fetching lead details data", e);
    }
  }

  updatedLeadData = (event: boolean) => {

    this.logger.debug("Updated", event);
    if (event) {
      this.getLeadData(event);
    }
  };


  getLeadData(isAfterUpdate: boolean){
    console.log('i m called', this.entry, this.program.program_code, this.selectedLead)
    this.leadDetailsService.getLeadDetails(this.entry, this.program.program_code, this.selectedLead, isAfterUpdate).subscribe(
      (data:any)=>{

        this.participantAssociations = data?.responsedata?.participantAssociations ?? [];

        if(this.entry === STATIC_DATA.PROVIDER_ENROLLMENT){
          this.filledInfo = data?.responsedata?.basicInfo;
          this.professionalLicense = data?.responsedata?.serviceDetails?.profLicenses;
        }else{
          this.filledInfo = data?.responsedata?.infoData;
          this.professionalLicense = data?.responsedata?.profLicenses;
          this.referralDataService.setDemographicData(
            data.responsedata?.infoData
          );
        }

        /* this.filledInfo = data?.responsedata?.infoData; */
        this.status = this.filledInfo?.applicationStatus;
        this.leadDetailsService.setLeadStatus(this.status);
        this.addressInfo = data?.responsedata?.communicationData?.permanentAddress;
        // const contactMethods = data?.responsedata?.communicationData?.contactMethods;
        this.contactDetailsInfo = data?.responsedata?.contactDetailsData;
        
        
        
        
        /*this.contactMethodsModifiedDate = this.getLastModifiedDate(contactMethods);

         const identifiers = data?.responsedata?.infoData?.identifiers;
        this.identifiersModifiedDate = this.getLastModifiedDate(identifiers); */

      }
    );
  }

  getLastModifiedDate(data : any[] ): string {
    if(data?.length){
      return data?.reduce((latest:any, current:any) => {
        const currentDate = new Date(current.modifiedDate);
        const latestDate = new Date(latest.modifiedDate);
        
        return currentDate > latestDate ? current : latest;
    }).modifiedDate;
    }else{
      return '';
    }
    
  }
}
